import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FileText, Download, Plus, Trash2, Edit2, Send, Check, X,
  Building2, Shield, DollarSign, Clock, ChevronDown, RefreshCw,
  Calculator, Package, AlertTriangle, CheckCircle2, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProposalGenerator = () => {
  const navigate = useNavigate();
  const { proposalId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [currentProposal, setCurrentProposal] = useState(null);
  const [clients, setClients] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeView, setActiveView] = useState('list');
  const [analytics, setAnalytics] = useState(null);
  
  const [newProposal, setNewProposal] = useState({
    clientId: '',
    assessmentId: '',
    title: '',
    description: '',
    includeRemediation: true,
    includeTraining: true,
    includeMonitoring: true,
    customServices: [],
    discountPercent: 0,
    validDays: 30,
    notes: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cset_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (proposalId) {
      fetchProposal(proposalId);
      setActiveView('detail');
    }
  }, [proposalId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [proposalsRes, clientsRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/proposals`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/msp/portfolio/overview`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/proposals/analytics/summary`, { headers: getAuthHeaders() })
      ]);

      if (proposalsRes.ok) {
        const data = await proposalsRes.json();
        setProposals(data.proposals || []);
      }

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients || []);
      }

      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProposal = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/proposals/${id}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentProposal(data);
      }
    } catch (err) {
      console.error('Failed to fetch proposal:', err);
    }
  };

  const fetchClientAssessments = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/api/assessments?organizationId=${clientId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAssessments(data.assessments || []);
      }
    } catch (err) {
      console.error('Failed to fetch assessments:', err);
    }
  };

  const handleClientChange = (clientId) => {
    setNewProposal({ ...newProposal, clientId, assessmentId: '' });
    if (clientId) {
      fetchClientAssessments(clientId);
    } else {
      setAssessments([]);
    }
  };

  const handleCreateProposal = async () => {
    if (!newProposal.clientId || !newProposal.title) {
      alert('Please select a client and enter a title');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/proposals`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newProposal)
      });

      if (response.ok) {
        const data = await response.json();
        setShowCreateModal(false);
        setNewProposal({
          clientId: '',
          assessmentId: '',
          title: '',
          description: '',
          includeRemediation: true,
          includeTraining: true,
          includeMonitoring: true,
          customServices: [],
          discountPercent: 0,
          validDays: 30,
          notes: ''
        });
        fetchData();
        navigate(`/proposals/${data.proposal.id}`);
      }
    } catch (err) {
      console.error('Failed to create proposal:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (id) => {
    setDownloading(true);
    try {
      const response = await fetch(`${API_URL}/api/proposals/${id}/download`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Proposal_${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleSendProposal = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/proposals/${id}/send`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchData();
        if (currentProposal?.id === id) {
          fetchProposal(id);
        }
      }
    } catch (err) {
      console.error('Failed to send proposal:', err);
    }
  };

  const handleAcceptProposal = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/proposals/${id}/accept`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchData();
        if (currentProposal?.id === id) {
          fetchProposal(id);
        }
      }
    } catch (err) {
      console.error('Failed to accept proposal:', err);
    }
  };

  const handleDeleteProposal = async (id) => {
    if (!confirm('Are you sure you want to delete this proposal?')) return;

    try {
      const response = await fetch(`${API_URL}/api/proposals/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchData();
        if (currentProposal?.id === id) {
          setCurrentProposal(null);
          setActiveView('list');
          navigate('/proposals');
        }
      }
    } catch (err) {
      console.error('Failed to delete proposal:', err);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'draft': return 'slate';
      case 'sent': return 'blue';
      case 'accepted': return 'emerald';
      case 'rejected': return 'red';
      case 'expired': return 'amber';
      default: return 'slate';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const ProposalCard = ({ proposal }) => {
    const statusColor = getStatusColor(proposal.status);
    
    return (
      <div 
        className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50 hover:border-violet-500/30 transition-all cursor-pointer"
        onClick={() => { setActiveView('detail'); navigate(`/proposals/${proposal.id}`); }}
        data-testid={`proposal-card-${proposal.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-xs text-slate-500 font-mono">{proposal.proposalNumber}</p>
            <h3 className="text-lg font-semibold text-white mt-1">{proposal.title}</h3>
            <p className="text-sm text-slate-400">{proposal.clientName}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium bg-${statusColor}-500/20 text-${statusColor}-400 border border-${statusColor}-500/30 capitalize`}>
            {proposal.status}
          </span>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-400">{proposal.services?.length || 0} services</span>
            <span className="text-slate-400">{proposal.totalHours || 0} hours</span>
          </div>
          <p className="text-xl font-bold text-violet-400">{formatCurrency(proposal.total)}</p>
        </div>

        <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
          <span>Created: {new Date(proposal.createdAt).toLocaleDateString()}</span>
          <span>Valid until: {new Date(proposal.validUntil).toLocaleDateString()}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading proposals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="proposal-generator-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Proposal Generator</h1>
                <p className="text-slate-400">Create and manage client proposals</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/msp')}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white hover:bg-slate-700 transition-colors"
            >
              <Building2 className="w-4 h-4" />
              Portfolio
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg transition-colors"
              data-testid="create-proposal-btn"
            >
              <Plus className="w-4 h-4" />
              New Proposal
            </button>
          </div>
        </div>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-2xl font-bold text-white">{analytics.totalProposals}</p>
            <p className="text-sm text-slate-400">Total Proposals</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-2xl font-bold text-emerald-400">{analytics.byStatus?.accepted || 0}</p>
            <p className="text-sm text-slate-400">Accepted</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-2xl font-bold text-blue-400">{analytics.byStatus?.sent || 0}</p>
            <p className="text-sm text-slate-400">Pending</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-2xl font-bold text-violet-400">{formatCurrency(analytics.totalValue || 0)}</p>
            <p className="text-sm text-slate-400">Total Pipeline</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <p className="text-2xl font-bold text-amber-400">{analytics.conversionRate || 0}%</p>
            <p className="text-sm text-slate-400">Conversion Rate</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      {activeView === 'list' && (
        <div className="max-w-7xl mx-auto">
          {proposals.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {proposals.map((proposal) => (
                <ProposalCard key={proposal.id} proposal={proposal} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Proposals Yet</h3>
              <p className="text-slate-400 mb-6">Create your first proposal to start generating revenue</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors"
              >
                Create First Proposal
              </button>
            </div>
          )}
        </div>
      )}

      {activeView === 'detail' && currentProposal && (
        <div className="max-w-5xl mx-auto">
          {/* Proposal Header */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-slate-500 font-mono mb-1">{currentProposal.proposalNumber}</p>
                <h2 className="text-2xl font-bold text-white">{currentProposal.title}</h2>
                <p className="text-slate-400 mt-1">Client: {currentProposal.clientName}</p>
              </div>
              <span className={`px-4 py-2 rounded-lg text-sm font-medium bg-${getStatusColor(currentProposal.status)}-500/20 text-${getStatusColor(currentProposal.status)}-400 border border-${getStatusColor(currentProposal.status)}-500/30 capitalize`}>
                {currentProposal.status}
              </span>
            </div>

            {currentProposal.description && (
              <p className="text-slate-300 mb-4">{currentProposal.description}</p>
            )}

            <div className="flex items-center gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Created: {new Date(currentProposal.createdAt).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Valid until: {new Date(currentProposal.validUntil).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Services Table */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 mb-6 overflow-hidden">
            <div className="p-4 border-b border-slate-700/50">
              <h3 className="text-lg font-semibold text-white">Proposed Services</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Service</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Hours</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Rate</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {currentProposal.services?.map((service, index) => (
                    <tr key={index} className="hover:bg-slate-800/50">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{service.name}</p>
                        <p className="text-xs text-slate-400">{service.description}</p>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-300">
                        {service.hours > 0 ? service.hours : '-'}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-300">
                        {service.hours > 0 ? `$${service.rate}/hr` : service.isRecurring ? `$${service.rate}/mo` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-white font-medium">
                        {formatCurrency(service.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="p-4 bg-slate-900/50 border-t border-slate-700/50">
              <div className="flex justify-end space-y-2">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Subtotal:</span>
                    <span className="text-white">{formatCurrency(currentProposal.subtotal)}</span>
                  </div>
                  {currentProposal.discountPercent > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Discount ({currentProposal.discountPercent}%):</span>
                      <span className="text-emerald-400">-{formatCurrency(currentProposal.discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-slate-700">
                    <span className="text-white font-semibold">Total:</span>
                    <span className="text-2xl font-bold text-violet-400">{formatCurrency(currentProposal.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => { setActiveView('list'); navigate('/proposals'); }}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Back to List
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={() => handleDeleteProposal(currentProposal.id)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>

              <button
                onClick={() => handleDownloadPDF(currentProposal.id)}
                disabled={downloading}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                <Download className={`w-4 h-4 ${downloading ? 'animate-bounce' : ''}`} />
                Download PDF
              </button>

              {currentProposal.status === 'draft' && (
                <button
                  onClick={() => handleSendProposal(currentProposal.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Mark as Sent
                </button>
              )}

              {currentProposal.status === 'sent' && (
                <button
                  onClick={() => handleAcceptProposal(currentProposal.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
                >
                  <Check className="w-4 h-4" />
                  Mark as Accepted
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">Create New Proposal</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Client Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Select Client *</label>
                <select
                  value={newProposal.clientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  data-testid="client-select"
                >
                  <option value="">Choose a client...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              {/* Assessment Selection */}
              {newProposal.clientId && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Link to Assessment (Optional)</label>
                  <select
                    value={newProposal.assessmentId}
                    onChange={(e) => setNewProposal({ ...newProposal, assessmentId: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  >
                    <option value="">No assessment linked</option>
                    {assessments.map((assessment) => (
                      <option key={assessment.id} value={assessment.id}>
                        {assessment.name} ({assessment.type}) - {assessment.progress || 0}% complete
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Linking an assessment auto-generates remediation items from gaps</p>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Proposal Title *</label>
                <input
                  type="text"
                  value={newProposal.title}
                  onChange={(e) => setNewProposal({ ...newProposal, title: e.target.value })}
                  placeholder="e.g., CMMC Compliance Remediation Services"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-violet-500"
                  data-testid="title-input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea
                  value={newProposal.description}
                  onChange={(e) => setNewProposal({ ...newProposal, description: e.target.value })}
                  placeholder="Brief overview of the proposed services..."
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-violet-500"
                />
              </div>

              {/* Service Options */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-3">Include Services</label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg cursor-pointer hover:bg-slate-900/70">
                    <input
                      type="checkbox"
                      checked={newProposal.includeRemediation}
                      onChange={(e) => setNewProposal({ ...newProposal, includeRemediation: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-600 text-violet-500 focus:ring-violet-500"
                    />
                    <div>
                      <p className="text-white font-medium">Remediation Services</p>
                      <p className="text-xs text-slate-400">Implementation and configuration assistance</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg cursor-pointer hover:bg-slate-900/70">
                    <input
                      type="checkbox"
                      checked={newProposal.includeTraining}
                      onChange={(e) => setNewProposal({ ...newProposal, includeTraining: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-600 text-violet-500 focus:ring-violet-500"
                    />
                    <div>
                      <p className="text-white font-medium">Security Awareness Training</p>
                      <p className="text-xs text-slate-400">Employee training program</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg cursor-pointer hover:bg-slate-900/70">
                    <input
                      type="checkbox"
                      checked={newProposal.includeMonitoring}
                      onChange={(e) => setNewProposal({ ...newProposal, includeMonitoring: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-600 text-violet-500 focus:ring-violet-500"
                    />
                    <div>
                      <p className="text-white font-medium">Continuous Monitoring</p>
                      <p className="text-xs text-slate-400">12-month monitoring subscription</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Discount */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Discount %</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={newProposal.discountPercent}
                    onChange={(e) => setNewProposal({ ...newProposal, discountPercent: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Valid for (days)</label>
                  <input
                    type="number"
                    min="7"
                    max="90"
                    value={newProposal.validDays}
                    onChange={(e) => setNewProposal({ ...newProposal, validDays: parseInt(e.target.value) || 30 })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Internal Notes</label>
                <textarea
                  value={newProposal.notes}
                  onChange={(e) => setNewProposal({ ...newProposal, notes: e.target.value })}
                  placeholder="Notes for the proposal..."
                  rows={2}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-violet-500"
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProposal}
                disabled={saving || !newProposal.clientId || !newProposal.title}
                className="flex items-center gap-2 px-6 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors disabled:opacity-50"
                data-testid="submit-proposal-btn"
              >
                <Calculator className="w-4 h-4" />
                {saving ? 'Creating...' : 'Generate Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalGenerator;
